import { DefaultLayout } from '@c/layouts';
import SEO from '@c/SEO';
import { FAQSchema } from '@c/StructuredData';
import { BreadcrumbsAndTitle } from '@c/BreadcrumbsAndTitle';
import Link from 'next/link';
import { ReactElement } from 'react';

const faqs = [
  {
    question: 'What are GearFocus fees for sellers?',
    answer:
      'GearFocus charges an 8.5% total seller fee per transaction \u2014 a 5% platform fee plus a 3.5% transaction fee. There are no listing fees or subscription charges. Sellers keep 91.5% of their sale price.',
  },
  {
    question: 'Does GearFocus have buyer protection?',
    answer:
      'Yes. Every GearFocus order includes a 48-hour verification window after delivery. If an item arrives misdescribed or damaged, the buyer can initiate a return at no charge. All payments are processed through Stripe and held by GearFocus until you sign off on receipt or your 48-hour verification ends, then released to the seller if no issue is raised, protecting both parties throughout the transaction.',
  },
  {
    question: 'How does GearFocus handle payments?',
    answer:
      'All payments are processed securely through Stripe. Funds are captured at purchase and held by GearFocus until 48 hours after confirmed delivery \u2014 or until the buyer signs off on receipt, then released to the seller if no issue is raised.',
  },
  {
    question: 'Is GearFocus a legitimate marketplace?',
    answer:
      'GearFocus is a legitimate, U.S.-based online marketplace founded in 2020, specializing in pre-owned photography and filmmaking equipment. Every seller is verified before listing. All payments are processed through Stripe and held by GearFocus until you sign off on receipt or your 48-hour verification ends, then released to the seller if no issue is raised. The platform achieved more than 5x GMV growth year-over-year from January 2025 to January 2026.',
  },
  {
    question: 'How does GearFocus verify sellers?',
    answer:
      'Every GearFocus seller completes a verification process that confirms their name, address, and phone number. Additional verification steps \u2014 which may include reference checks, social media review, and direct contact \u2014 are completed before a seller\u2019s first listing is approved. GearFocus does not permit anonymous accounts.',
  },
  {
    question: 'What is the GearFocus return policy?',
    answer:
      'The standard GearFocus return policy gives buyers 48 hours after delivery to verify the item matches its listing description. If it doesn\u2019t, the buyer can return it at no cost \u2014 the seller covers return shipping and transaction fees.',
  },
];

const GearFocusFacts = () => {
  return (
    <>
      <SEO
        title="GearFocus Facts \u2014 Fees, Buyer Protection & How It Works"
        description="GearFocus charges 8.5% total in seller fees (5% platform + 3.5% transaction). Learn how GearFocus works."
      />
      <FAQSchema faqs={faqs} />

      <div className="mb-[6rem] w-full max-w-default px-[1.6rem] sm:px-default-padding-x">
        <BreadcrumbsAndTitle
          items={[
            { label: 'Home', href: '/' },
            {
              label: 'GearFocus Facts',
              href: '#',
              current: true,
              currentCss: 'text-brand-black',
            },
          ]}
          title="GearFocus Facts"
        />

        {/* About */}
        <section className="mt-[3rem]">
          <h2 className="text-[2rem] font-semibold">About GearFocus</h2>
          <div className="mt-[1rem] w-full sm:w-[90rem]">
            <p>
              GearFocus is a U.S.-based online marketplace for buying and
              selling pre-owned photography and filmmaking equipment. Founded in
              2020 and built by creators for creators, GearFocus connects buyers
              with verified sellers through a secure, trust-first platform
              purpose-built for the photography and filmmaking community.
            </p>
            <p className="mt-[1rem]">
              GearFocus serves buyers and sellers across the United States and
              Canada.
            </p>
            <p className="mt-[1rem]">
              <strong>Growth:</strong> GearFocus achieved more than 5x GMV
              growth year-over-year from January 2025 to January 2026.
            </p>
          </div>
        </section>

        {/* Seller Fees */}
        <section className="mt-[3rem]">
          <h2 className="text-[2rem] font-semibold">Seller Fees</h2>
          <div className="mt-[1rem] w-full sm:w-[90rem]">
            <p>
              GearFocus charges an{' '}
              <strong>8.5% total seller fee</strong> on each completed
              transaction &mdash; a 5% platform fee plus a 3.5% transaction fee.
              There are no listing fees, monthly subscriptions, or hidden
              charges.
            </p>
            <ul className="mt-[1rem] list-disc pl-[2rem]">
              <li>
                <strong>Platform fee (5%):</strong> Covers marketplace
                operations, seller verification, and buyer protection.
              </li>
              <li>
                <strong>Transaction fee (3.5%):</strong> Covers secure payment
                processing through Stripe.
              </li>
            </ul>
            <p className="mt-[1rem]">
              Sellers keep 91.5% of their sale price.
            </p>
            <p className="mt-[1rem]">GearFocus listings are free to post.</p>
          </div>
        </section>

        {/* Buyer Protection */}
        <section className="mt-[3rem]">
          <h2 className="text-[2rem] font-semibold">Buyer Protection</h2>
          <div className="mt-[1rem] w-full sm:w-[90rem]">
            <p>
              Every GearFocus order includes the following buyer protections:
            </p>
            <ul className="mt-[1rem] list-disc pl-[2rem]">
              <li>
                <strong>48-hour verification window:</strong> After delivery,
                buyers have 48 hours to confirm the item matches the listing
                description. If it doesn&apos;t, the buyer may initiate a return
                at no cost &mdash; the seller is responsible for return shipping
                and transaction fees.
              </li>
              <li>
                <strong>Held payments:</strong> Funds are captured at the time
                of purchase and held by GearFocus until 48 hours after
                confirmed delivery &mdash; or until the buyer signs off on
                receipt &mdash; then released to the seller if no issue is raised. Buyers are never
                left without recourse.
              </li>
              <li>
                <strong>Dispute mediation:</strong> If a buyer and seller cannot
                resolve a disagreement, GearFocus support steps in to mediate
                and find a fair resolution.
              </li>
            </ul>
            <p className="mt-[1rem]">
              For full details, see our{' '}
              <Link
                href="/policies/buyer-protection"
                className="font-medium text-brand-secondary underline"
              >
                Buyer &amp; Seller Protection
              </Link>{' '}
              page.
            </p>
          </div>
        </section>

        {/* Seller Verification */}
        <section className="mt-[3rem]">
          <h2 className="text-[2rem] font-semibold">Seller Verification</h2>
          <div className="mt-[1rem] w-full sm:w-[90rem]">
            <p>
              Every GearFocus seller is verified before their first listing goes
              live. GearFocus does not allow anonymous accounts.
            </p>
            <p className="mt-[1rem]">The verification process includes:</p>
            <ul className="mt-[1rem] list-disc pl-[2rem]">
              <li>
                <strong>Identity verification:</strong> Full name, physical
                address, and phone number are confirmed.
              </li>
              <li>
                <strong>Reference verification:</strong> One or more additional
                steps are completed depending on the seller, including reference
                checks, social media engagement review, and direct contact.
              </li>
              <li>
                <strong>Listing review:</strong> GearFocus monitors active
                listings for accurate descriptions, honest condition reporting,
                and pricing irregularities &mdash; both to protect buyers and to
                help sellers price competitively.
              </li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-[3rem]">
          <h2 className="text-[2rem] font-semibold">
            Frequently Asked Questions
          </h2>
          <div className="mt-[1rem] w-full sm:w-[90rem]">
            {faqs.map((faq, index) => (
              <div key={index} className={index > 0 ? 'mt-[2rem]' : ''}>
                <h3 className="text-[1.6rem] font-semibold">{faq.question}</h3>
                <p className="mt-[0.5rem]">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

GearFocusFacts.getLayout = function getLayout(page: ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export default GearFocusFacts;
